import { useEffect, useRef, useState } from "react";
import type { Machine } from "../services/BackendDataService";
import {
  getMotorMonitoringService,
  destroyMotorMonitoringService,
  peekMotorMonitoringService,
  type MotorState,
  type StreamInfoCallback,
} from "../services/MotorMonitoringService";
import { useUnifiedAuth } from "../auth/useUnifiedAuth";
import { getActiveOrgAlias } from "../services/demoMode";
import { natsClient } from "../services/NATSClient";

export interface UseMotorStatesOptions {
  /** Called once with the stream's oldest/newest available timestamps. */
  onStreamInfo?: StreamInfoCallback;
  /** Minimum ms between overview history points during Doris replay. */
  sampleIntervalMs?: number;
}

export interface UseMotorStatesResult {
  /** Map of axisId → MotorState (live signals incl. diagMsg, history, …). */
  motors: Map<string, MotorState>;
  /** True once the live NATS websocket is connected. */
  isConnected: boolean;
  /** True once the subscription request has resolved. */
  subscribed: boolean;
  /** True while historical data is being replayed. */
  replaying: boolean;
  /** Machine serial number (NATS subject key), or undefined. */
  machineBK?: string;
  /** Organisation alias resolved from the auth token, or null. */
  orgAlias: string | null;
}

/**
 * Subscribe to a machine's motor/servo axis stream and expose the live axis map.
 *
 * Single shared entry point for every axis-based condition-monitoring screen
 * (Motor cards, Servo status tree, …). It mirrors the energy rule:
 *   - live  (no startTime) → NATS worker; requires the live websocket.
 *   - range (startTime set) → Doris; works even when the live stream is empty
 *     or disconnected, exactly like the energy screen.
 * Consumers just read `motors` and render.
 */
export function useMotorStates(
  machineData?: Machine | null,
  startTime?: string,
  paused = false,
  options: UseMotorStatesOptions = {},
): UseMotorStatesResult {
  const { onStreamInfo, sampleIntervalMs } = options;
  const { token } = useUnifiedAuth();

  const [motors, setMotors] = useState<Map<string, MotorState>>(new Map());
  const [isConnected, setIsConnected] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [replaying, setReplaying] = useState(false);

  // Read the latest `paused` inside the once-captured NATS callback.
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  const machineBK = machineData?.maschinennummer;
  const orgAlias = token ? getActiveOrgAlias(token) : null;
  const hasRange = !!startTime;

  // Track NATS connection state.
  useEffect(() => {
    setIsConnected(natsClient.isConnected());
    const cb = (status: { isConnected: boolean }) =>
      setIsConnected(status.isConnected);
    natsClient.onStatusChange(cb);
    return () => natsClient.offStatusChange(cb);
  }, []);

  // Subscribe to the axis stream. Live needs the websocket; a date range goes
  // to Doris and must work regardless of the live connection (energy rule), so
  // `hasRange` is a dependency: selecting a range while offline still loads.
  useEffect(() => {
    if (!machineBK || !orgAlias) return;
    if (!isConnected && !startTime) return;

    const service = getMotorMonitoringService(machineBK);
    if (onStreamInfo) service.onStreamInfo(onStreamInfo);

    const callback = (map: Map<string, MotorState>, isReplaying: boolean) => {
      if (pausedRef.current) return;
      setMotors(new Map(map));
      setReplaying(isReplaying);
    };

    setSubscribed(false);
    service
      .subscribe(
        orgAlias,
        machineBK,
        callback,
        startTime,
        sampleIntervalMs,
        token ?? undefined,
      )
      .then(() => setSubscribed(true));

    return () => {
      service.unsubscribe(callback);
      if (onStreamInfo) service.offStreamInfo(onStreamInfo);
      destroyMotorMonitoringService(machineBK);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machineBK, orgAlias, isConnected, hasRange]);

  // React to date-range changes (e.g. 1d → 3d) without tearing down the service.
  useEffect(() => {
    if (!machineBK) return;
    if (!isConnected && !startTime) return;
    peekMotorMonitoringService(machineBK)?.setDateRange(
      startTime,
      sampleIntervalMs,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machineBK, isConnected, startTime, sampleIntervalMs]);

  return { motors, isConnected, subscribed, replaying, machineBK, orgAlias };
}
